import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/common/Logo";
import { PartnerLoginButton } from "@/components/common/PartnerLoginButton";
import { NAV_LINKS } from "@/constants/site";
import { cn } from "@/lib/utils";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full border-b border-halalgo-kraft-100 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70 transition-shadow duration-300",
        scrolled ? "shadow-lift-sm" : "shadow-none"
      )}
      data-testid="site-navbar"
    >
      <nav
        className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8"
        aria-label="Primary"
      >
        <Logo />

        {/* Desktop links */}
        <div className="hidden items-center gap-8 md:flex">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={link.testId}
              className={({ isActive }) =>
                cn(
                  "nav-underline text-sm font-medium text-halalgo-ink/75 transition-colors hover:text-halalgo-green-800",
                  isActive && "is-active font-semibold text-halalgo-green-800"
                )
              }
            >
              {link.label}
            </NavLink>
          ))}
        </div>

        {/* Desktop Partner Login */}
        <div className="hidden md:flex">
          <PartnerLoginButton />
        </div>

        {/* Mobile trigger */}
        <div className="flex md:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Open menu"
                data-testid="nav-mobile-open-button"
                className="text-halalgo-ink hover:bg-halalgo-kraft-50"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[86%] max-w-sm border-halalgo-kraft-100 bg-halalgo-paper"
              data-testid="nav-mobile-menu"
            >
              <SheetHeader className="text-left">
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <Logo />
              </SheetHeader>

              <div className="mt-8 flex flex-col gap-1">
                {NAV_LINKS.map((link) => (
                  <SheetClose asChild key={link.to}>
                    <NavLink
                      to={link.to}
                      end={link.to === "/"}
                      data-testid={`${link.testId}-mobile`}
                      className={({ isActive }) =>
                        cn(
                          "rounded-md px-3 py-3 text-base font-medium text-halalgo-ink transition-colors hover:bg-halalgo-kraft-50",
                          isActive && "bg-halalgo-green-50 text-halalgo-green-800"
                        )
                      }
                    >
                      {link.label}
                    </NavLink>
                  </SheetClose>
                ))}
              </div>

              <div className="mt-6 border-t border-halalgo-kraft-100 pt-6">
                <PartnerLoginButton fullWidth />
              </div>

              <p className="mt-6 text-xs font-medium text-halalgo-kraft-700">
                Halal Made Easy • Launching soon
              </p>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
